import React from 'react';
import { Zap, Leaf, ShieldCheck, CheckCircle2 } from 'lucide-react';

// Electrical + Environmental specifications, dropped into Process & Rates and machine pages.
const ELECTRICAL = {
  icon: Zap,
  eyebrow: 'Electrical specifications',
  title: 'Built to pass inspection',
  points: [
    'We use cRUus-listed (Recognized) components so your rebuilt machine is ready for ETL field certification.',
    'Field certification is valid for North America only (Canada and the USA).',
    'Wiring, controls and safety circuits are prepared for a field evaluation and field label where required.',
    'Correct grounding, breaker sizing and voltage matching for your service (120V or 240V).',
    'Clean, code-minded electrical work you can put in front of an inspector or insurer.',
  ],
};

const ENVIRONMENTAL = {
  icon: Leaf,
  eyebrow: 'Environmental specifications',
  title: 'Responsible refrigerants',
  points: [
    'Refrigerants selected for lower GWP (global warming potential), in step with the Montreal Protocol and its Kigali Amendment.',
    'Refrigerant is recovered, handled and recharged responsibly during every rebuild.',
    'Phase-down-friendly choices help future proof your machine against changing regulations.',
    'A smaller environmental footprint without giving up cooling performance.',
  ],
};

const Column = ({ spec, testid }) => {
  const Icon = spec.icon;
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-8" data-testid={testid}>
      <span className="w-14 h-14 rounded-xl bg-[#CE8946]/15 flex items-center justify-center mb-5">
        <Icon className="h-7 w-7 text-[#B87333]" />
      </span>
      <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">{spec.eyebrow}</span>
      <h3 className="text-2xl font-bold text-[#003456] mt-2">{spec.title}</h3>
      <ul className="mt-5 space-y-3">
        {spec.points.map((p, i) => (
          <li key={i} className="flex items-start gap-3 text-slate-600 text-sm leading-relaxed">
            <CheckCircle2 className="h-5 w-5 text-[#CE8946] flex-shrink-0 mt-0.5" /> {p}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SpecsSection = ({ bg = 'bg-[#F1F5F9]' }) => (
  <section className={`py-20 section-padding ${bg} scroll-mt-24`} id="specifications" data-testid="specs-section">
    <div className="max-w-7xl mx-auto">
      <div className="max-w-2xl mb-10">
        <span className="inline-flex items-center gap-2 text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">
          <ShieldCheck className="h-4 w-4" /> Standards &amp; compliance
        </span>
        <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Electrical &amp; environmental specifications</h2>
        <p className="text-slate-600 mt-3">
          Every rebuild is done with certification and the environment in mind, so the machine that ships back to
          you is safe, inspectable and responsible.
        </p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6 items-start">
        <Column spec={ELECTRICAL} testid="specs-electrical" />
        <Column spec={ENVIRONMENTAL} testid="specs-environmental" />
      </div>
    </div>
  </section>
);

export default SpecsSection;
